import React, { ChangeEvent, FormEvent, useLayoutEffect, useRef, useState, useEffect } from 'react';
import { Box, Button, IconButton, TextField, Typography, buttonClasses, styled, useTheme } from '@mui/material';
import { Mic, MicOff, Stop } from '@mui/icons-material';
import ImageComponent from 'ui/components/shared/ImageComponent';
import SendIcon from 'assets/icons/send-Filled_1_.png';
import ThumbUpIcon from 'assets/icons/Thumbs Up.png';
import ThumbDownIcon from 'assets/icons/Thumb Down.png';
import ChatBubble from './bubble/ChatBubble';
import { useTranslation } from 'react-i18next';
import ReportModal from './modal/ReportModal';
import { ChatComponentPropType, FeedbackInterface, ParagraphInterface, MultipleChoiceData } from 'entities/interfaces';
import useFontSize from 'hooks/FontSize';
import { SubmitButton } from '../shared/SubmitButton';
import ChatLoader from './ChatLoader';
import BookmarkIcon from 'assets/icons/Bookmark.png';
import BookmarkDarkIcon from 'assets/icons/bookmark-dark.png';
import BookmarkFilledIcon from 'assets/icons/bookmark-filled.png';
import { Loop } from '@mui/icons-material';
import useScreenSize from 'hooks/ScreenSize';
import { MobileWidth } from 'entities/constants';
import MultipleChoice from './MultipleChoice';
import { parseHTMLToJSON } from 'utils/urlDecoder';
import DragAndDrop from './DragAndDrop';
import SortTheParagraph from './SortTheParagraph';
import { HTML5Backend } from "react-dnd-html5-backend";
import MobileChatLoader from '../mobile/skills/MobileChatLoader';

type ChatMessage = ParagraphInterface & { id: number; type: string };

const Wrapper = styled(Box)(() => ({
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    flexShrink: 0,
}));

const TypeBox = styled('form')(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'light' ? theme.palette.common.white : theme.palette.grey[800],
    height: '5rem',
    display: 'flex',
    alignItems: 'center',
    padding: '0.56rem 0.56rem 0.56rem 2rem',
    borderRadius: '624.9375rem',
    gap: '1rem',
    flexShrink: 0,
    margin: '0.5rem 5rem 1rem 5rem',
    boxShadow: '0px 0px 16px rgba(0, 0, 0, 0.1)',
}));

// Modern Microphone Button für Desktop - innerhalb der TypeBox
const MicrophoneButton = styled(IconButton)<{ isRecording?: boolean }>(({ theme, isRecording }) => ({
    backgroundColor: isRecording ? '#FF4757' : 'transparent',
    borderRadius: '50%',
    width: '3rem',
    height: '3rem',
    minWidth: '3rem',
    minHeight: '3rem',
    color: isRecording ? '#FFFFFF' : theme.palette.text.secondary,
    border: 'none',
    padding: '0.5rem',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    animation: isRecording ? 'pulse 1.5s infinite' : 'none',
    
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        background: isRecording 
            ? 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 100%)'
            : 'transparent',
        borderRadius: '50%',
        transition: 'all 0.3s ease'
    },
    
    '&:hover': {
        backgroundColor: isRecording ? '#FF3742' : theme.palette.action.hover,
        color: isRecording ? '#FFFFFF' : theme.palette.primary.main,
        transform: 'scale(1.05)',
        
        '&::before': {
            background: isRecording 
                ? 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 100%)'
                : `linear-gradient(135deg, ${theme.palette.primary.main}20 0%, ${theme.palette.primary.main}10 100%)`
        }
    },
    
    '&:active': {
        transform: 'scale(0.95)',
        transition: 'all 0.15s ease'
    },
    
    '@keyframes pulse': {
        '0%': {
            boxShadow: '0 0 0 0 rgba(255, 71, 87, 0.4)'
        },
        '70%': {
            boxShadow: '0 0 0 10px rgba(255, 71, 87, 0)'
        },
        '100%': {
            boxShadow: '0 0 0 0 rgba(255, 71, 87, 0)'
        }
    }
}));

// Recording Status Indicator für Desktop
const RecordingIndicator = styled(Box)<{ isVisible: boolean }>(({ isVisible }) => ({
    display: isVisible ? 'flex' : 'none',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: 'rgba(255, 71, 87, 0.1)',
    borderRadius: '20px',
    margin: '0 5rem 8px 5rem',
    border: '1px solid rgba(255, 71, 87, 0.2)',
    color: '#FF4757',
    fontSize: '14px',
    fontWeight: '500',
    animation: 'fadeIn 0.3s ease-in-out',
    
    '@keyframes fadeIn': {
        from: { opacity: 0, transform: 'translateY(10px)' },
        to: { opacity: 1, transform: 'translateY(0)' }
    }
}));

const TitleBox = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.common.white,
    height: '3.75rem',
    display: 'flex',
    alignItems: 'center',
    padding: '0 1rem',
    borderRadius: '0.5rem',
    flexShrink: 0,
    gap: '0.75rem',
    position: 'relative',
    zIndex: 10,
    overflow: 'visible',
}));

const StartOverBox = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.common.white,
    height: '3.75rem',
    display: 'flex',
    alignItems: 'center',
    padding: '0 1rem',
    borderRadius: '0.5rem',
    flexShrink: 0,
    gap: '0.75rem',
    position: 'relative',
    zIndex: 10,
    marginTop: '0.2rem',
}));

const TitleText = styled(Typography)(({ theme }) => ({
    fontWeight: 400,
    color: theme.palette.common.white,
    flexGrow: 1,
    lineHeight: '1.25rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
    maxWidth: '100%',
}));

const TitleNum = styled(TitleText)(({ theme }) => ({
    width: '1.98rem',
    height: '1.98rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontWeight: 400,
    borderRadius: '50%',
    border: `2px solid ${theme.palette.common.white}`,
    flexGrow: 0,
}))

const FeedbackBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    gap: '1.2rem',
    alignItems: 'center',
    paddingTop: '1rem',
}));

const FeedbackText = styled(Typography)(({ theme }) => ({
    fontWeight: 'bolder',
}));

const FeedbackButtonBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    gap: '1.6rem',
}));

const FeedbackButton = styled(Button)(({ theme }) => ({
    borderRadius: '2rem',
    textTransform: 'none',
    padding: '0.25rem 3rem',
    fontWeight: 'bolder',
    height: '2.5rem',
}));

// Mobile-spezifische Styles
const MobileTypeBox = styled('form')({
    backgroundColor: '#FFFFFF',
    borderRadius: '24px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    padding: '12px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    margin: '8px 12px 12px 12px',
    border: '1px solid #E4E6EA',
    minHeight: '44px',
    maxHeight: '44px',
    
    // Responsive für sehr kleine Bildschirme
    '@media (max-width: 320px)': {
        margin: '6px 8px 8px 8px', // Reduzierte Margins für iPhone SE
        padding: '8px 12px',
        gap: '8px',
        minHeight: '40px',
        maxHeight: '40px',
        borderRadius: '20px',
    }
});

const MobileMicButton = styled(IconButton)({
    backgroundColor: 'transparent',
    borderRadius: '50%',
    width: '36px',
    height: '36px',
    minWidth: '36px',
    minHeight: '36px',
    color: '#8696A0',
    border: 'none',
    padding: '6px',
    opacity: 0.4,
    cursor: 'not-allowed',
    
    '&:hover': {
        backgroundColor: 'rgba(134, 150, 160, 0.1)',
    },
});

const SendButton = styled(Button)(({ theme }) => ({
    borderRadius: '50%',
    width: '3.875rem',
    height: '3.875rem',
    fontSize: '2.8rem',
    padding: 0,
    minHeight: 0,
    minWidth: 0,
}));

const ChatBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    borderRadius: '1.2rem',
    flexGrow: 1,
}));

const ContentBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    overflowY: 'scroll',
    padding: '5rem 1.12rem 0',
    [theme.breakpoints.up('md')]: {
        '&::-webkit-scrollbar': {
            width: '0.4rem',
            height: '0.4rem',
        },
        '&::-webkit-scrollbar-track': {
            background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
            background: theme.palette.primary.dark,
            borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
            background: theme.palette.grey[700],
        },
        marginRight: '0.1rem',
    },
}));

interface ChatComponentProps extends Omit<ChatComponentPropType, 'messagesData'> {
    messagesData: Array<FeedbackInterface | ParagraphInterface>;
    mobileMode?: boolean;
}

const ChatComponent = ({
    messagesData,
    onUserInput,
    sendMessage,
    date,
    chapter,
    isInModal,
    typingNotAllowed,
    isHome,
    handleFeedback,
    handleNextChapter,
    bookmarkChapter,
    reportAllowed,
    sendDisabled,
    isLoading,
    handleNext,
    clearProgress,
    reportParagraph,
    typingAllowed,
    onSubmit,
    handleSaveProgress,
    clickedIndexes,
    setClickedIndexes,
    npsSubmitted,
    setNpsSubmitted,
    mobileMode = false, // Neuer Prop für Mobile Mode
}: ChatComponentProps) => {
    const fontSize = useFontSize();
    const { width } = useScreenSize();
    const theme = useTheme();
    const { t } = useTranslation();
    const [reportParagraphId, setReportParagraphId] = useState('');
    const [reportModalVisible, setReportModalVisible] = useState(false);
    const [messageText, setMessageText] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const [apiRequestData, setApiRequestData] = useState<{ context: string; prompt: string; userInput: string } | null>(null);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [selectedNps, setSelectedNps] = useState<number | null>(null);
    
    // Speech-to-Text States
    const [isRecording, setIsRecording] = useState(false);
    const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);

    useEffect(() => {
        localStorage.setItem('clickedIndexes', JSON.stringify(Array.from(clickedIndexes)));
    }, [clickedIndexes]);

    const getNpsColor = (num: number) => {
        if (num >= 0 && num <= 6) return "#FF6B6B";
        if (num >= 7 && num <= 8) return "#FFCA3A";
        if (num >= 9 && num <= 10) return "#2ECC71";
        return "grey";
    };

    const handleNextButtonClick = (id: number, outputs: string) => {
        setClickedIndexes(prev => {
            const updated = new Set(prev);
            updated.add(id);
            setTimeout(() => {
                handleNext?.(outputs);
            }, 0);
            return updated;
        });
    };

    const filteredMessages = messagesData.filter((msg, index, self) => {
        if (!('type' in msg) || msg.type !== 'next') return true;
        const lastNextIndex = [...self]
            .reverse()
            .findIndex(m => 'type' in m && m.type === 'next');
        const actualLastIndex = self.length - 1 - lastNextIndex;
        return index === actualLastIndex;
    });

    useLayoutEffect(() => {
        if (containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
    }, [messagesData, isLoading]);

    const formatDate = (date: Date) => {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const months = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December',
        ];

        const day = days[date.getDay()];
        const month = months[date.getMonth()];
        const dateNumber = date.getDate();
        const suffix =
            dateNumber === 1 || dateNumber === 11 || dateNumber === 21 || dateNumber === 31
                ? 'st'
                : dateNumber === 2 || dateNumber === 12 || dateNumber === 22
                    ? 'nd'
                    : 'th';

        return `${day}, ${month} ${dateNumber}${suffix}`;
    };

    function isFeedbackInterface(obj: any): obj is FeedbackInterface {
        return 'feedback' in obj;
    }

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (messageText.trim()) {
            onUserInput(messageText.trim());
            setMessageText('');
        }
    };

    // Enter-Taste Handler für Mobile
    const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            if (messageText.trim()) {
                onUserInput(messageText.trim());
                setMessageText('');
            }
        }
    };

    function isParagraphInterface(message: FeedbackInterface | ParagraphInterface): message is ParagraphInterface {
        return 'type' in message && 'content' in message && typeof message.content === 'object';
    }

    function hasContent(message: FeedbackInterface | ParagraphInterface): message is ParagraphInterface {
        return (message as ParagraphInterface).content !== undefined;
    }

    function isMultipleChoiceData(content: any): content is MultipleChoiceData {
        return (
            content &&
            typeof content === 'object' &&
            'question' in content &&
            'options' in content &&
            'maxPoints' in content &&
            'negativePoints' in content &&
            'feedback' in content
        );
    }

    // Speech-to-Text Funktionalität
    const handleMicrophoneClick = async () => {
        if (!isRecording) {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                const recorder = new MediaRecorder(stream);
                const audioChunks: BlobPart[] = [];

                recorder.ondataavailable = (event) => {
                    audioChunks.push(event.data);
                };

                recorder.onstop = async () => {
                    const audioBlob = new Blob(audioChunks, { type: 'audio/wav' });
                    
                    // Hier würdest du normalerweise die Audio-Datei an einen Speech-to-Text Service senden
                    console.log('Audio aufgenommen:', audioBlob);
                    
                    // Placeholder: Simuliere erkannten Text
                    const simulatedText = "Erkannter Sprach-Text würde hier erscheinen";
                    setMessageText(prev => prev + (prev ? ' ' : '') + simulatedText);
                    
                    // Stream stoppen
                    stream.getTracks().forEach(track => track.stop());
                };

                recorder.start();
                setMediaRecorder(recorder);
                setIsRecording(true);
            } catch (error) {
                console.error('Mikrofon-Zugriff fehlgeschlagen:', error);
                alert('Mikrofon-Zugriff nicht möglich. Bitte überprüfen Sie Ihre Browser-Einstellungen.');
            }
        } else {
            // Aufnahme stoppen
            if (mediaRecorder) {
                mediaRecorder.stop();
                setMediaRecorder(null);
            }
            setIsRecording(false);
        }
    };

    return (
        <Wrapper>
            <ContentBox ref={containerRef}>
                {/*date && */(
                    <>
                        <StartOverBox>
                            <Typography fontWeight={400} fontSize={fontSize.body} flexGrow={1}>
                                {/*formatDate(date)*/}
                            </Typography>
                            {clearProgress && chapter && (
                                <SubmitButton
                                    variant="outlined"
                                    fontColor={theme.palette.text.primary}
                                    marginTop="0"
                                    minWidth="10rem"
                                    icon={<Loop fontSize="small" />}
                                    onClick={clearProgress}
                                >
                                    Start Over
                                </SubmitButton>
                            )}
                        </StartOverBox>
                        <TitleBox sx={{ backgroundColor: theme.palette.primary.main, color: theme.palette.common.white }}>
                            {chapter && (
                                <>
                                    {chapter.thumbnail && (
                                        <img
                                            src={chapter.thumbnail}
                                            alt="image"
                                            style={{ width: '2.8rem', height: '2.8rem', }}
                                        />
                                    )}
                                    <TitleText fontSize={fontSize.body}>{t(chapter.title)}</TitleText>
                                </>
                            )}
                        </TitleBox>
                    </>
                )}

                <ChatBox padding={isHome ? '1.25rem 3rem ' : '1.25rem 0 0 0'}>
                    {filteredMessages.map((message, index) => {
                        const key = message.id;
                        if (isParagraphInterface(message) && message.type === 'mc') {
                            const content = message.content as unknown as MultipleChoiceData;
                            let progressState = null;
                            if (message.progressState) {
                                progressState = message.progressState;
                            }
                            return (
                                <ChatBubble
                                    key={key}
                                    date={message.time}
                                    message={<MultipleChoice
                                        question={content.question}
                                        options={content.options}
                                        maxPoints={content.maxPoints}
                                        negativePoints={content.negativePoints}
                                        feedback={content.feedback}
                                        onChange={(selectedOptions) => console.log('Selected options:', selectedOptions)}
                                        isSingleChoice={content.isSingleChoice}
                                        onSubmit={onSubmit}
                                        onSaveProgress={handleSaveProgress}
                                        data={{
                                            selectedOptions: message.progressState?.selectedOptions ?? [],
                                            points: message.progressState?.points ?? 0
                                        }}
                                    />}
                                    isLeft={true}
                                />
                            );
                        } else if (
                            isParagraphInterface(message) && message.type === 'io' && [message.title === 'Multiple Choice' || message.title === 'Multiple-Choice']) {
                            const content = message.content as unknown as MultipleChoiceData;
                            let progressState = null;
                            if (message.progressState) {
                                progressState = message.progressState;
                            }

                            return (
                                <ChatBubble
                                    key={key}
                                    date={message.time}
                                    message={
                                        <MultipleChoice
                                            question={content.question}
                                            options={content.options}
                                            maxPoints={content.maxPoints}
                                            negativePoints={content.negativePoints}
                                            feedback={content.feedback}
                                            onChange={(selectedOptions) => console.log('Selected options:', selectedOptions)}
                                            isSingleChoice={content.isSingleChoice}
                                            onSubmit={onSubmit}
                                            onSaveProgress={handleSaveProgress}
                                            data={{
                                                selectedOptions: message.progressState?.selectedOptions ?? [],
                                                points: message.progressState?.points ?? 0
                                            }}
                                        />
                                    }
                                    isLeft={true}
                                />
                            );
                        }
                        else if (
                            isParagraphInterface(message) && message.type === 'io' && message.title === 'GPT'
                        ) {
                            const jsonObject = parseHTMLToJSON(message.content);
                            return (
                                <ChatBubble
                                    key={key}
                                    date={message.time}
                                    message={jsonObject}
                                    isLeft={true}
                                />
                            );
                        }
                        else if (hasContent(message) && message.type === 'io' && (message.title === "Drag-and-Drop" || message.title === "Drag-and-Drop-Multiple")) {
                            return (
                                <ChatBubble
                                    key={key}
                                    date={message.time}
                                    disabled={index < filteredMessages.length - 2 || isLoading}
                                    message={<DragAndDrop
                                        htmlContent={message.content}
                                        onSubmit={onSubmit}
                                        // @ts-ignore
                                        onSaveProgress={handleSaveProgress}
                                        data={message.progressState || { placedElements: {}, points: 0 }}
                                    />}
                                    isLeft={true}
                                    handleNext={handleNext}
                                />
                            );
                        }
                        else if (hasContent(message) && message.type === 'io' && (message.title === "Sort-the-Paragraph" || message.title === "Sort")) {
                            return (
                                <ChatBubble
                                    key={key}
                                    date={message.time}
                                    disabled={index < filteredMessages.length - 2 || isLoading}
                                    message={
                                        <SortTheParagraph
                                            htmlContent={message.content}
                                            onSubmit={onSubmit}
                                            onSaveProgress={(data) => handleSaveProgress({ ...data, selectedOptions: data.selectedOptions ?? [] })}
                                            data={{
                                                placedElements: message.progressState?.placedElements ?? {},
                                                points: message.progressState?.points ?? 0
                                            }}
                                        />
                                    }
                                    isLeft={true}
                                    handleNext={handleNext}
                                />
                            );
                        }
                        else if (isFeedbackInterface(message) || message.type === 'io') {
                            return (
                                <ChatBubble
                                    key={key}
                                    date={message.time}
                                    disabled={index < filteredMessages.length - 2 || isLoading}
                                    message={isFeedbackInterface(message) ? message.feedback : message.content}
                                    isLeft={isFeedbackInterface(message) ? message.direction === '0' : true}
                                    outputs={!isFeedbackInterface(message) && message.outputs}
                                    handleNext={handleNext}
                                />
                            );
                        }
                        if (message.type === 'next') {
                            return (
                                !clickedIndexes.has(message.id) && (
                                    <SubmitButton
                                        key={key}
                                        width="7.25rem"
                                        height="2.5rem"
                                        margin="0.5rem 3.8rem"
                                        marginTop="0.5rem"
                                        disabled={index < filteredMessages.length - 1 || isLoading}
                                        onClick={() => handleNextButtonClick(message.id, message.outputs)}
                                    >
                                        {t("Next")}
                                    </SubmitButton>
                                )
                            );
                        } else if (message.type === 'chat') {
                            return (
                                <ChatBubble
                                    key={key}
                                    date={message.time}
                                    disabled={index < filteredMessages.length - 2 || isLoading}
                                    message={isFeedbackInterface(message) ? message.feedback : message.content}
                                    isLeft={isFeedbackInterface(message) ? message.direction === '0' : true}
                                    outputs={!isFeedbackInterface(message) && message.outputs}
                                    handleNext={handleNext}
                                />
                            );
                        }
                        else if (message.type === 'userInput') {
                            const userMessage = typeof message.content === 'string' && message.content.trim() !== ""
                                ? message.content
                                : '[Leere Eingabe]';

                            return (
                                <ChatBubble
                                    key={key}
                                    date={message.time}
                                    message={userMessage}
                                    isLeft={false}
                                    disabled={index < filteredMessages.length - 2 || isLoading}
                                />
                            );
                        }
                        else if (message.type === 'response') {
                            return (
                                <ChatBubble
                                    key={key}
                                    date={message.time}
                                    disabled={index < filteredMessages.length - 2 || isLoading}
                                    message={message.content || "[Keine Antwort erhalten]"}
                                    isLeft={true}
                                    handleNext={handleNext}
                                />
                            );
                        } else if (message.type === "exit") {
                            return chapter?.isLastSection ? (
                                !npsSubmitted ? (
                                    <FeedbackBox key={key}>
                                        <FeedbackText fontSize={fontSize.title3}>
                                            {t("How likely are you to recommend this course?")}
                                        </FeedbackText>

                                        <FeedbackButtonBox>
                                            {Array.from({ length: 11 }, (_, num) => (
                                                <Button
                                                    key={num}
                                                    variant={selectedNps === num ? "contained" : "outlined"}
                                                    onClick={() => setSelectedNps(num)}
                                                    sx={{
                                                        backgroundColor: selectedNps === num ? getNpsColor(num) : "transparent",
                                                        color: selectedNps === num ? "white" : "black",
                                                        border: `2px solid ${getNpsColor(num)}`,
                                                        minWidth: "3rem",
                                                        minHeight: "3rem",
                                                        fontSize: "1.2rem",
                                                        borderRadius: "8px",
                                                    }}
                                                >
                                                    {num}
                                                </Button>
                                            ))}
                                        </FeedbackButtonBox>

                                        <SubmitButton
                                            width="10rem"
                                            height="2.5rem"
                                            margin="1rem 0"
                                            disabled={index < filteredMessages.length - 1 || isLoading}
                                            onClick={() => {
                                                if (selectedNps !== null) {
                                                    handleFeedback && handleFeedback(message.section_id, selectedNps);
                                                    setNpsSubmitted(true);
                                                    console.log("🔄 NPS abgegeben:", selectedNps);
                                                }
                                            }}
                                        >
                                            {t("Submit")}
                                        </SubmitButton>
                                    </FeedbackBox>
                                ) : (
                                    <>
                                        <ChatBubble key={key} message={t("Danke für dein Feedback! 😊")} isLeft={true} />
                                        <SubmitButton
                                            width="13rem"
                                            height="2.5rem"
                                            margin="0.5rem 3.8rem"
                                            marginTop="0.5rem"
                                            onClick={() => window.location.href = "/skillary"}
                                        >
                                            {t("goToSkillary")}
                                        </SubmitButton>
                                    </>
                                )
                            ) : (
                                <SubmitButton
                                    key={key}
                                    width="12.5rem"
                                    height="2.5rem"
                                    margin="0.5rem 3.8rem"
                                    marginTop="0.5rem"
                                    disabled={index < filteredMessages.length - 1 || isLoading}
                                    onClick={() => handleNextChapter && handleNextChapter(message.section_id)}
                                >
                                    {t("Next Chapter")}
                                </SubmitButton>
                            );
                        }

                        return null;
                    })}
                    {isLoading && (
                        width <= MobileWidth ? (
                            <MobileChatLoader />
                        ) : (
                            <ChatLoader isInModal={isInModal} />
                        )
                    )}
                </ChatBox>
            </ContentBox>

            {/* Recording Indicator */}
            <RecordingIndicator isVisible={isRecording && !mobileMode}>
                <Stop sx={{ fontSize: '16px' }} />
                {t('Aufnahme läuft... Klicken Sie erneut, um zu stoppen')}
            </RecordingIndicator>

            {/* Desktop Input Form */}
            {!mobileMode && typingAllowed && !typingNotAllowed && (
                <TypeBox onSubmit={handleSubmit}>
                    <TextField
                        placeholder={t('Type a message...')}
                        fullWidth
                        autoComplete="off"
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        variant="standard"
                        InputProps={{
                            sx: {
                                fontSize: fontSize.body,
                                color: theme.palette.text.primary,
                                letterSpacing: '0.02rem',
                            },
                            disableUnderline: true,
                        }}
                    />
                    
                    {/* Modern Microphone Button - innerhalb der TypeBox */}
                    <MicrophoneButton
                        aria-label="microphone"
                        isRecording={isRecording}
                        onClick={handleMicrophoneClick}
                    >
                        {isRecording ? (
                            <Stop sx={{ fontSize: '1.5rem' }} />
                        ) : (
                            <Mic sx={{ fontSize: '1.5rem' }} />
                        )}
                    </MicrophoneButton>
                    
                    <SendButton
                        variant="contained"
                        type="submit"
                        disabled={!messageText.trim()}
                    >
                        <ImageComponent src={SendIcon} alt="send" width="1.5rem" height="1.5rem" />
                    </SendButton>
                </TypeBox>
            )}
            {!mobileMode && typingAllowed === false && !typingNotAllowed && (
                <TypeBox onSubmit={handleSubmit}>
                    <TextField
                        placeholder={t('Type a message...')}
                        fullWidth
                        autoComplete="off"
                        onChange={(e) => setMessageText(e.target.value)}
                        variant="standard"
                        disabled={true}
                        InputProps={{
                            sx: {
                                fontSize: fontSize.body,
                                color: theme.palette.text.primary,
                                letterSpacing: '0.02rem',
                            },
                            disableUnderline: true,
                        }}
                    />
                    
                    {/* Disabled Microphone Button */}
                    <MicrophoneButton
                        aria-label="microphone"
                        disabled
                        sx={{ opacity: 0.5 }}
                    >
                        <Mic sx={{ fontSize: '1.5rem' }} />
                    </MicrophoneButton>
                    
                    <SendButton
                        variant="contained"
                        type="submit"
                        disabled={true}
                    >
                        <ImageComponent src={SendIcon} alt="send" width="1.5rem" height="1.5rem" />
                    </SendButton>
                </TypeBox>
            )}

            {/* Mobile Input Form - ohne Send Button */}
            {mobileMode && typingAllowed && (
                <MobileTypeBox onSubmit={handleSubmit}>
                    <TextField
                        placeholder={t('Nachricht eingeben...')}
                        fullWidth
                        autoComplete="off"
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        onKeyPress={handleKeyPress}
                        variant="standard"
                        InputProps={{
                            sx: {
                                fontSize: '16px',
                                color: '#111B21',
                                letterSpacing: '0.01rem',
                                '&::placeholder': {
                                    color: '#8696A0',
                                    opacity: 1,
                                }
                            },
                            disableUnderline: true,
                        }}
                        sx={{
                            '& .MuiInputBase-input': {
                                padding: '12px 0',
                                '&::placeholder': {
                                    color: '#8696A0',
                                    opacity: 1,
                                }
                            }
                        }}
                    />
                    
                    {/* Deaktiviertes Mikrofon für Mobile */}
                    <MobileMicButton
                        aria-label="microphone"
                        disabled
                    >
                        <Mic sx={{ fontSize: '20px' }} />
                    </MobileMicButton>
                </MobileTypeBox>
            )}

            {/* Mobile Input Form - Disabled State */}
            {mobileMode && !typingAllowed && (
                <MobileTypeBox onSubmit={handleSubmit}>
                    <TextField
                        placeholder={t('Nachricht eingeben...')}
                        fullWidth
                        autoComplete="off"
                        onChange={(e) => setMessageText(e.target.value)}
                        onKeyPress={handleKeyPress}
                        variant="standard"
                        disabled={true}
                        InputProps={{
                            sx: {
                                fontSize: '16px',
                                color: '#111B21',
                                letterSpacing: '0.01rem',
                                '&::placeholder': {
                                    color: '#8696A0',
                                    opacity: 1,
                                }
                            },
                            disableUnderline: true,
                        }}
                        sx={{
                            '& .MuiInputBase-input': {
                                padding: '12px 0',
                                '&::placeholder': {
                                    color: '#8696A0',
                                    opacity: 1,
                                }
                            }
                        }}
                    />
                    
                    {/* Deaktiviertes Mikrofon für Mobile */}
                    <MobileMicButton
                        aria-label="microphone"
                        disabled
                    >
                        <Mic sx={{ fontSize: '20px' }} />
                    </MobileMicButton>
                </MobileTypeBox>
            )}
            
            <Typography
                variant="caption"
                color="textSecondary"
                sx={{
                    fontSize: '0.85rem',
                    textAlign: 'center',
                    marginTop: '-0.5rem',
                    opacity: 0.8
                }}
            >
                {t('KI kann Fehler machen. Keine vertraulichen oder personenbezogenen Daten eingeben.')}
            </Typography>

            {reportParagraph && reportAllowed && reportModalVisible && (
                <ReportModal
                    closeModal={() => {
                        setReportModalVisible(false);
                        setReportParagraphId('');
                    }}
                    handleReport={(content: string) => reportParagraph(reportParagraphId, content)}
                />
            )}
        </Wrapper>
    );
};

export default ChatComponent;